import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';

import Adhesion from '../../../atoms/adhesion';
import AssetAllocation from '../../../molecules/profile/asset-allocation';
import CardResource from '../../../molecules/profile/card-resource';
import CardResourceContainer from '../../../molecules/profile/card-resource-container';
import CardResourceMoneyMarketHoldings from '../../../molecules/profile/card-resource-money-market-holdings';
import FeesAndExpensesExpenseCharge from '../../../molecules/profile/fees-and-expenses-expense-charge';
import FooterDisclosures from '../../../atoms/footer-disclosures';
import HeaderQuoteBar from '../../../molecules/profile/header-quote-bar';
import HypotheticalGrowth from '../../../molecules/profile/hypothetical-growth';
import InvestmentFacts from '../../../molecules/profile/investment-facts';
import InvestmentStrategy from '../../../molecules/profile/investment-strategy';
import MaturityAllocation from '../../../molecules/profile/maturity-allocation';
import MoneyMarketChart from '../../../molecules/profile/money-market-chart';
import PageHeader from '../../../molecules/profile/page-header';
import PageMain from '../../../molecules/profile/page-main';
import PageSideRail, { DisclosurePrint } from '../../../molecules/profile/page-side-rail';
import PerformanceReturns from '../../../molecules/profile/performance-returns';
import PortfolioManagement from '../../../molecules/profile/portfolio-management';
import RisksDisclosure from '../../../molecules/profile/risks-disclosure';
import SectionDivider from '../../../atoms/section-divider';
import SectorDiversification from '../../../molecules/profile/sector-diversification';
import TemplateSectionDisclosure from '../../../atoms/template-section-disclosures';
import sectionDividerOptions from '../../../../helpers/statics/section-divider-options';
import transformInvestmentFacts from '../../../../stores/profile/modules/transform-investment-facts';
import useStore from '../../../../stores/profile/use-variable-annuity-cref-money-market-store';
import { className } from '../../../../utils/GlobalCssProxy';
import { getIsDynamicGreetingShown } from '../../../molecules/profile/dynamic-greeting/dynamic-greeting-helper';
import useDetectPrint from 'react-detect-print';
import { checkIsCrefR4 } from '../../../../utils/sharedHelpers';

const VariableAnnuityCrefMoneyMarket = () => {
  const store = useStore();
  const isCrefR4 = checkIsCrefR4(store.xref);
  const quoteData = store.quote.isLoaded ? store.quote.data : {};
  const xrefData = store.xref.isLoaded ? store.xref.data : {};
  const maturityAllocationData = store.maturityAllocation.isLoaded
    ? store.maturityAllocation.data
    : [];
  const participantHoldingsData = (store.participantHoldings && store.participantHoldings.isLoaded)
    ? store.participantHoldings.data
    : {};
  const showDynamicGreeting = participantHoldingsData && getIsDynamicGreetingShown(participantHoldingsData);

  const disclosureList = store.disclosureList.isLoaded ? store.disclosureList.data : [];

  const printing = useDetectPrint();
  const printingFeesExpenses = [disclosureList.find(item => { return item?.component === 'performance-returns'; })];
  return (
    <div data-template="VariableAnnuityCrefMoneyMarket">
      <PageHeader
        displayAsFundType="Variable Annuity"
        sectionNavOptions={[
          sectionDividerOptions.performance,
          sectionDividerOptions.allocation,
          sectionDividerOptions.characteristics,
          sectionDividerOptions.resources
        ]}
      >
        <HeaderQuoteBar>
          <HeaderQuoteBar.UnitValue
            value={quoteData.priceLast}
            asOfDate={quoteData.priceAsOfDate}
          />
          <HeaderQuoteBar.DayChange
            value={quoteData.priceChange}
            changePercent={quoteData.priceChangePercent}
          />
          <HeaderQuoteBar.YtdReturn
            value={quoteData.yearToDateReturn}
            asOfDate={quoteData.yearToDateReturnAsOfDate}
            responsive={printing ? undefined : 1}
            isCrefR4={isCrefR4}
          />
          <HeaderQuoteBar.EstimatedExpenseCharge
            value={quoteData.estimatedExpenseCharge}
            responsive={printing ? undefined : 2}
          />
          <HeaderQuoteBar.SevenDayCurrentEffectiveYield
            currentYield={quoteData['7DayYieldCurrent']}
            effectiveYield={quoteData['7DayYieldEffective']}
            asOfDate={quoteData['7DayYieldAsOfDate']}
            responsive={printing ? undefined : 3}
          />
          <HeaderQuoteBar.AssetClass responsive={printing ? undefined : 4}>
            {quoteData.assetClass}
          </HeaderQuoteBar.AssetClass>
        </HeaderQuoteBar>
      </PageHeader>
      <PageMain>
        <SectionDivider
          labelText={sectionDividerOptions.performance.label}
          id={sectionDividerOptions.performance.id}
          isDynamicGreetingShown={showDynamicGreeting}
          dynamicGreetingParticipantHoldings={participantHoldingsData}
        >
          <Row>
            <Col>
              <PerformanceReturns {...store.performanceReturns} />
            </Col>
          </Row>
          <Row>
            <Col>
              <DisclosurePrint disclosureList={printingFeesExpenses} avoidPageBreak />
            </Col>
          </Row>
          <Row>
            <Col md={8} {...className('col-print-12')}>
              <HypotheticalGrowth />
            </Col>
            <Col md={4} {...className('d-none', 'd-md-block', 'no-print')}>
              <Adhesion.Performance />
            </Col>
          </Row>
          <Row>
            <Col md={4} {...className('no-print')}>
              <FeesAndExpensesExpenseCharge
                isHideForPrint={true}
                expenses={
                  store.feesExpenses.isLoaded ? store.feesExpenses.data : {}
                }
                heading="Fees &amp; Expenses"
              />
            </Col>
            <Col md={8} {...className('col-print-12')}>
              <MoneyMarketChart.LiquidAssets />
            </Col>
          </Row>
          <Row>
            <Col md={6} {...className('col-print-6')}>
              <MoneyMarketChart.NetFlows />
            </Col>
            <Col md={6} {...className('col-print-6')}>
              <MoneyMarketChart.MarketValueDaily />
            </Col>
          </Row>
          <Row>
            <Col>
              <RisksDisclosure />
            </Col>
          </Row>
        </SectionDivider>
        <SectionDivider
          labelText={sectionDividerOptions.allocation.label}
          id={sectionDividerOptions.allocation.id}
        >
          <Row>
            <Col md={4} {...className('col-print-12')}>
              <AssetAllocation
                allocations={
                  store.assetAllocation.isLoaded
                    ? store.assetAllocation.data.items
                    : []
                }
                asOfDate={
                  store.assetAllocation.isLoaded
                    ? store.assetAllocation.data.asOfDate
                    : null
                }
                ticker={xrefData.symbol || null}
              />
            </Col>
            <Col {...className('col-print-12')}>
              <SectorDiversification ticker={xrefData.symbol || null} />
            </Col>
          </Row>
          <Row>
            <Col md={8} {...className('col-print-12')}>
              <MaturityAllocation
                maturityAllocations={maturityAllocationData}
                ticker={xrefData.symbol}
              />
            </Col>
            <Col {...className('d-none', 'd-md-block')}>
              <Adhesion.Allocation />
            </Col>
          </Row>
          <Row>
            <Col>
              <TemplateSectionDisclosure section="allocation" />
            </Col>
          </Row>
        </SectionDivider>
        <SectionDivider
          labelText={sectionDividerOptions.characteristics.label}
          id={sectionDividerOptions.characteristics.id}
        >
          <Row>
            <Col md={4} {...className('col-print-12')}>
              <InvestmentStrategy
                heading="Investment Description"
                {...(store.investmentStrategy.data || {})}
              >
                {store.investmentStrategy.isLoaded
                  ? store.investmentStrategy.data.investmentStrategy
                  : ''}
              </InvestmentStrategy>
            </Col>
            <Col {...className('d-none', 'd-md-block')}>
              <Adhesion.Characteristics />
            </Col>
          </Row>
          <Row>
            <Col md={6} {...className('col-print-12')}>
              <PortfolioManagement />
            </Col>
            <Col {...className('col-print-12')}>
              <InvestmentFacts
                header="Account Facts"
                facts={
                  store.investmentFacts.isLoaded
                    ? transformInvestmentFacts(
                      store.investmentFacts.data,
                      store.fundType,
                      store.assetClass
                    )
                    : [] // prettier-ignore
                }
                ticker={xrefData.symbol ? xrefData.symbol : null}
              ></InvestmentFacts>
            </Col>
          </Row>
        </SectionDivider>
        <SectionDivider
          labelText={sectionDividerOptions.resources.label}
          id={sectionDividerOptions.resources.id}
        >
          <CardResourceContainer>
            <CardResource.SimpleLink
              bodyText="CREF Accounts"
              data-analytics-componenttype="column-component"
              data-analytics-id="COL_Learn"
              data-analytics-section="component"
              href="https://www.tiaa.org/public/retire/financial-products/annuities/retirement-plan-annuities/cref-accounts"
              iconVariant="Learn"
              titleText="Learn"
            />
            <CardResource.SimpleLink
              bodyText="Lifetime Income Calculator"
              data-analytics-componenttype="column-component"
              data-analytics-id="COL_Tools"
              data-analytics-section="component"
              href="https://shared.tiaa.org/public/publictools/targetincome/annuityEstimator"
              iconVariant="Calculator"
              titleText="Tools"
            />
            <CardResource.SimpleLink
              bodyText="N-MFP"
              data-analytics-componenttype="column-component"
              data-analytics-id="COL_Forms"
              data-analytics-section="component"
              href="https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=0000777535&type=N-MFP&dateb=&count=20&scd=filings"
              iconVariant="Link"
              titleText="Form"
            />
            <CardResourceMoneyMarketHoldings />
          </CardResourceContainer>
        </SectionDivider>
        <FooterDisclosures />
        <div {...className('admaster-number', 'd-print-none')}>X-3240867P-Q0923W</div>
        <DisclosurePrint disclosureList = {disclosureList} admasterNumber="X-3240867P-Q0923W" />
      </PageMain>
      <PageSideRail />
    </div>
  );
};

export default VariableAnnuityCrefMoneyMarket;
